import { skyOrchidOpaqueMeshes } from '../../../packages/domain/src/131-sky-orchid-orbit.mjs';
import { SKY_WORLD, SKY_WORLD_ASSETS } from '../../../packages/domain/src/86-sky-world-scale.mjs';
import { skyFlightPointToWorld } from '../../../packages/domain/src/92-sky-flight-frame.mjs';
import { skyAuthoredPoseContact } from '../../../packages/domain/src/115-sky-authored-pose.mjs';

/** Close physical-orbit composition prioritizes the pilot and current source
 * objective. It never shrinks the rider to fit the entire enormous bloom.
 * Opaque source triangles shorten the camera boom during real curtain passes. */
export function skyOrchidOrbitCameraFrame(state, aspect, pointFov) {
  const basis = state.flightFrame,
    boss = state.boss,
    orchid = boss.orchid;
  const waters = boss.authoredProps.filter((p) => p.orchidWaterfall);
  const objective = orchid.pollen
    ? [...waters].sort(
        (a, b) =>
          Math.hypot(a.x - state.x, a.z - state.pilotZ) -
          Math.hypot(b.x - state.x, b.z - state.pilotZ),
      )[0]
    : boss.weakpoint;
  const shoulder = aspect < 1 ? 0.45 : 0.9;
  const eye = skyFlightPointToWorld(basis, { x: 0, y: 0.7, z: 0 });
  let position = skyFlightPointToWorld(basis, { x: shoulder, y: 1.15, z: 5.85 });
  for (const prop of waters) {
    const meshes = skyOrchidOpaqueMeshes(prop.authoredRig);
    const hit = skyAuthoredPoseContact(prop.authoredPose, eye, position, meshes, 0.18);
    if (hit) {
      const fraction = Math.max(0.1, hit.fraction - 0.06);
      position = Object.fromEntries(
        ['x', 'y', 'z'].map((a) => [a, eye[a] + (position[a] - eye[a]) * fraction]),
      );
    }
  }
  const unit = (p) => {
    const delta = ['x', 'y', 'z'].map((a) => p[a] - position[a]);
    const length = Math.hypot(...delta) || 1;
    return delta.map((v) => v / length);
  };
  const sole = state.y - SKY_WORLD.pilotAimHeight;
  const pilot = { x: state.x, y: sole + SKY_WORLD_ASSETS.pilot.height / 2, z: state.pilotZ };
  const a = unit(pilot),
    b = unit(objective);
  const frame = {
    ...position,
    lookX: position.x + a[0] + b[0],
    lookY: position.y + a[1] + b[1],
    lookZ: position.z + a[2] + b[2],
    fov: aspect < 1 ? 64 : 58,
    near: 0.1,
    maskWeight: 0,
    firstPerson: false,
    up: { ...basis.up },
  };
  for (const p of [
    objective,
    { ...pilot, y: sole },
    { ...pilot, y: sole + SKY_WORLD_ASSETS.pilot.height },
  ])
    frame.fov = Math.max(
      frame.fov,
      Math.min(aspect < 1 ? 110 : 100, pointFov(frame, p, aspect, 0.9)),
    );
  return frame;
}
